import { useState, useEffect, useCallback } from 'react'
import { useAIChat } from '../../hooks/useAIChat'
import { useRealtimeAI } from '../../hooks/useRealtimeAI'
import { useVideoMode } from '../../hooks/useVideoMode'
import { useSocket } from '../../contexts/SocketContext'
import { useConversations } from '../../hooks/useConversations'

// Components
import ChatHeader from '../AIInspect/ChatHeader'
import ChatSidebar from '../AIInspect/ChatSidebar'
import ChatMessages from '../AIInspect/ChatMessages'
import ChatInput from '../AIInspect/ChatInput'
import PromptCards from '../AIInspect/PromptCards'
import EmptyState from '../AIInspect/EmptyState'
import VideoMode from '../AIInspect/VideoMode'
import VoiceMode from '../AIInspect/VoiceMode'
import RealtimeMode from '../AIInspect/RealtimeMode'

export default function AIInspect() {
  // UI State
  const [showSidebar, setShowSidebar] = useState(false)
  const [showAttachMenu, setShowAttachMenu] = useState(false)
  
  // Custom Hooks
  const {
    messages,
    inputText,
    threadId: chatThreadId,           // ✅ PHASE 1: Get threadId from chat hook
    setInputText,
    messagesEndRef,
    addMessage,
    handleSendMessage,
    handleFileUpload,
    handleKeyPress,
    clearMessages,
    setConversationThread,             // ✅ PHASE 1: Get method to set threadId
    clearThread: clearChatThread       // ✅ PHASE 1: Get method to clear threadId
  } = useAIChat()

  const { loadConversation } = useConversations()

  const {
    isVoiceMode,
    isRealtimeMode,
    isListening,
    transcript,
    userTranscript,
    aiTranscript,
    isConnecting,
    threadId: realtimeThreadId,      // ✅ PHASE 1: Get threadId from realtime
    conversationId: realtimeConversationId,
    startVoiceMode,
    startRealtimeMode,
    toggleListening,
    endVoiceMode,
    endRealtimeMode,
    handleRealtimeImageUpload,
    sendTextMessage,                 // ✅ PHASE 3: Get text message sending
    clearThread: clearRealtimeThread // ✅ PHASE 1: Get clearThread method
  } = useRealtimeAI()

  const {
    isVideoMode,
    isInspectionMode,
    videoRef,
    startVideoMode,
    startInspectionMode,
    endVideoMode,
    captureAndAnalyze
  } = useVideoMode()

  const { realtimeSocket } = useSocket()

  // ✅ PHASE 1: Bidirectional threadId synchronization between chat and realtime
  useEffect(() => {
    // Realtime → Chat: When switching from realtime to chat, pass the threadId
    if (!isRealtimeMode && realtimeThreadId && realtimeThreadId !== chatThreadId) {
      console.log(`💬 [AIInspect] Sync threadId from realtime to chat: ${realtimeThreadId.substring(0, 8)}...`);
      setConversationThread(realtimeThreadId);
    }
    
    // Chat → Realtime: When chatThreadId changes (e.g., from localStorage), ensure both hooks have it
    // This handles the case when page loads with saved threadId
    if (!isRealtimeMode && chatThreadId && !realtimeThreadId) {
      console.log(`💬 [AIInspect] Chat has threadId but realtime doesn't: ${chatThreadId.substring(0, 8)}...`);
      // Realtime hook will pick it up from localStorage on next connect
    }
  }, [isRealtimeMode, realtimeThreadId, chatThreadId, setConversationThread]);

  // Listen for professional matches from backend
  useEffect(() => {
    if (realtimeSocket) {
      const handleProfessionalsMatched = (data) => {
        console.log('📊 Professionals matched:', data);
        
        // Add professional cards to chat
        addMessage({
          type: 'professional_cards',
          professionals: data.professionals,
          criteria: data.criteria,
          recommendation: data.recommendation,
          searchQuery: data.searchQuery,
          noResults: data.noResults,
          hasExternalResults: data.hasExternalResults,
          externalCount: data.externalCount,
          dbCount: data.dbCount,
          timestamp: new Date()
        });
      };

      realtimeSocket.on('professionals_matched', handleProfessionalsMatched);
      
      return () => {
        realtimeSocket.off('professionals_matched', handleProfessionalsMatched);
      };
    }
  }, [realtimeSocket, addMessage]);

  // Event Handlers
  const handlePromptSelect = (text) => {
    setInputText(text)
    setTimeout(handleUnifiedSendMessage, 100)  // ✅ PHASE 3: Use unified handler
  }

  const handleStartVoice = () => {
    startVoiceMode(addMessage)
  }

  const handleStartVideo = () => {
    startVideoMode(addMessage)
  }

  // ✅ PHASE 1 FIX: Pass existing chatThreadId when starting realtime mode
  const handleStartRealtime = () => {
    // Pass the current chatThreadId to continue the conversation
    startRealtimeMode(addMessage, null, chatThreadId || realtimeThreadId)
  }

  // Start Inspection Mode (integrates video + realtime AI)
  const handleStartInspection = () => {
    // First, ensure we're in realtime mode
    if (!isRealtimeMode) {
      // ✅ PHASE 1 FIX: Pass existing chatThreadId to continue conversation
      startRealtimeMode(addMessage, () => {
        // After realtime mode is established, start inspection
        setTimeout(() => {
          startInspectionMode(addMessage, realtimeSocket)
        }, 1000)
      }, chatThreadId || realtimeThreadId)
    } else {
      // Already in realtime, just start inspection
      startInspectionMode(addMessage, realtimeSocket)
    }
  }

  /**
   * ✅ PHASE 3 FIX: Handle ending session based on mode
   * Voice/Realtime mode: Just toggle UI to text mode, keep WebRTC alive
   * Video mode: End video, return to realtime
   * Only fully disconnect when "New Chat" or "Close" is clicked
   */
  const handleEndSession = useCallback(() => {
    // Always stop video if active
    if (isVideoMode) {
      endVideoMode(addMessage)
    }

    // Fully end realtime session and return to chat view
    if (isVoiceMode || isRealtimeMode) {
      console.log('🛑 Ending realtime session and returning to chat...');
      endVoiceMode(addMessage)
      endRealtimeMode(addMessage)
    }
  }, [isVideoMode, isVoiceMode, isRealtimeMode, endVideoMode, endVoiceMode, endRealtimeMode, addMessage])

  /**
   * ✅ PHASE 3: Unified message sending - uses WebRTC when available
   * This is the key change that enables text mode to use Realtime API!
   */
  const handleUnifiedSendMessage = async () => {
    if (!inputText.trim()) return;

    const message = inputText;
    setInputText(''); // Clear input immediately for better UX

    // Add user message to UI
    addMessage({
      sender: 'user',
      text: message,
      type: 'text',
      timestamp: new Date()
    });

    try {
      // ✅ NEW: Use WebRTC if connected (seamless text/voice mode)
      if (isRealtimeMode && sendTextMessage) {
        console.log('📤 [Unified Send] Using WebRTC Realtime API for text');
        await sendTextMessage(message);
        // AI response will come through WebRTC events (already handled)
      }
      // ❌ FALLBACK: Use Chat Completions API (old way)
      else {
        console.log('📤 [Unified Send] Using Chat Completions API (fallback)');
        await handleSendMessage();
      }
    } catch (error) {
      console.error('❌ Error sending message:', error);
      addMessage({
        sender: 'system',
        text: 'Failed to send message. Please try again.',
        type: 'error',
        timestamp: new Date()
      });
    }
  };

  /**
   * Handle selecting a conversation from sidebar
   * Loads all messages and resumes the conversation
   */
  const handleSelectConversation = async (id, idType = 'auto') => {
    try {
      console.log(`📂 Loading conversation: ${id} (type: ${idType})`);
      
      // Clear current messages
      clearMessages();
      
      // Load messages from backend (supports both conversationId and sessionId)
      const loadedMessages = await loadConversation(id, idType);
      
      console.log(`✅ Loaded ${loadedMessages.length} messages`);
      
      // Add loaded messages to chat
      loadedMessages.forEach(msg => {
        addMessage({
          sender: msg.role === 'user' ? 'user' : msg.role === 'assistant' ? 'ai' : msg.role === 'ai' ? 'ai' : 'system',
          text: msg.content,
          type: msg.contentType || 'text',
          timestamp: new Date(msg.sentAt)
        });
      });
      
      // Start realtime mode with context
      // ✅ PHASE 1 FIX: Pass the loaded conversation's thread ID
      await startRealtimeMode(addMessage, null, id);
      
      console.log(`✅ Resumed conversation ${id}`);
    } catch (error) {
      console.error('❌ Failed to load conversation:', error);
      addMessage({
        sender: 'system',
        text: 'Failed to load conversation. Please try again.',
        type: 'error',
        timestamp: new Date()
      });
    }
  }

  /**
   * ✅ PHASE 1: Enhanced "New Chat" - Clear all state and start fresh
   */
  const handleNewChat = () => {
    console.log('🆕 Starting new chat - clearing all state');
    
    // 1. Close realtime connection if open
    if (isRealtimeMode) {
      console.log('📴 Closing realtime connection...');
      endRealtimeMode();
    }
    
    // 2. Clear messages from UI
    clearMessages();
    
    // 3. Clear threadId from both hooks and localStorage
    if (clearChatThread) clearChatThread();
    if (clearRealtimeThread) clearRealtimeThread();
    
    console.log('✅ New chat started - ready for fresh conversation');
  };

  // Render different modes
  if (isVideoMode) {
    return (
      <VideoMode
        videoRef={videoRef}
        onEndSession={handleEndSession}
      />
    )
  }

  if (isRealtimeMode) {
            return (
              <RealtimeMode
                messages={messages}
                messagesEndRef={messagesEndRef}
                isListening={isListening}
                transcript={transcript}
                userTranscript={userTranscript}
                aiTranscript={aiTranscript}
                isConnecting={isConnecting}
                onToggleListening={toggleListening}
                onStartVideo={handleStartInspection}
                onEndSession={handleEndSession}
                onImageUpload={handleRealtimeImageUpload}
                isInspectionMode={isInspectionMode}
                videoRef={videoRef}
                isVideoMode={isVideoMode}
                onCaptureFrame={add => captureAndAnalyze(add, realtimeSocket)}
              />
            )
          }

  // Regular chat mode
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <ChatHeader onToggleSidebar={() => setShowSidebar(!showSidebar)} />

      {/* Sidebar */}
      <ChatSidebar
        showSidebar={showSidebar}
        onCloseSidebar={() => setShowSidebar(false)}
        onNewChat={handleNewChat}
        onSelectConversation={handleSelectConversation}
      />

      {/* Main content area - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {messages.length === 0 ? (
          <EmptyState
            onVideoMode={handleStartVideo}
            onVoiceMode={handleStartVoice}
          />
        ) : (
          <ChatMessages
            messages={messages}
            messagesEndRef={messagesEndRef}
          />
                      )}
                    </div>
                    
      {/* Bottom input area - Sticky */}
      <div className="sticky bottom-0 z-20 p-4 bg-white">
        {/* Prompt Cards - only show when no messages */}
        <PromptCards
          onPromptSelect={handlePromptSelect}
          showCards={messages.length === 0}
        />

        {/* Input */}
        <ChatInput
          inputText={inputText}
          setInputText={setInputText}
          onSendMessage={handleUnifiedSendMessage}
          onKeyPress={handleKeyPress}
          onVoiceMode={handleStartVoice}
          onFileUpload={handleFileUpload}
          onVideoMode={handleStartVideo}
          onRealtimeMode={handleStartRealtime}
          showAttachMenu={showAttachMenu}
          setShowAttachMenu={setShowAttachMenu}
        />
            
            <div className="text-xs text-gray-500 text-center mt-2">
              AI Inspect can make mistakes. Check important info.
        </div>
      </div>

    </div>
  )
}